import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { CSSProperties } from "react";
import styled from "styled-components";
import {
  Lesson,
  isSchoolUserTeacher,
  hasAgeRange,
  didProfileCompleteLesson,
} from "../common/api";
import { ageRange } from "../common/utils";
import { faShareNodes } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { Account } from "../common/context/AccountContext";
import {
  SURFACE_CARD_RADIUS,
  SURFACE_CARD_RADIUS_SMALL,
} from "../common/constants";
import { SPACE_3XS, SPACE_XS, SPACE_MD, SPACE_LG } from "../common/spacing";
import {
  CellCover,
  CellMetaItem,
  CellMetaRow,
  CELL_COVER_SIZE,
  CELL_COVER_SIZE_COMPACT,
  clampLines,
} from "../common/CommonStyles";
import {
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_GREEN,
} from "../common/colors";
import { LessonBadges } from "./lesson/LessonBadges";

const COVER_MARGIN_END = SPACE_LG;
const COVER_MARGIN_END_COMPACT = SPACE_MD;
const ATTRIBUTE_SPACING = SPACE_LG;

const StyledLessonItemBody = styled.div`
  display: flex;
  flex-direction: row;
`;

const Cover = styled(CellCover)<{ $marginEnd: number; $radius: number }>`
  margin-right: ${(p) => p.$marginEnd}px;
  border-radius: ${(p) => p.$radius}px;
`;

const MetadataBody = styled.div<{ $height: number }>`
  flex: 1;
  min-width: 0;
  height: ${(p) => p.$height}px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  ${clampLines(1)}
  color: ${COLOR_TEXT_PRIMARY};
  font-weight: 500;
  font-size: 16px;
`;

const Description = styled.div`
  ${clampLines(2)}
  font-size: 12px;
  line-height: 14px;
  color: ${COLOR_TEXT_SECONDARY};
  margin-top: ${SPACE_3XS}px;
`;

const MetaRow = styled(CellMetaRow)`
  margin-top: auto;
  line-height: 1;
`;

const CompletedItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE_XS}px;
  color: ${COLOR_GREEN};
  font-size: 11px;
  font-weight: 600;
`;

const CompletedIcon = styled.img`
  display: block;
  height: 1.1em;
  width: auto;
`;

export const CompletedMetaItem = () => {
  const { t } = useTranslation();
  return (
    <CompletedItem>
      <CompletedIcon src={`${process.env.PUBLIC_URL}/check-green.png`} alt="" />
      {t("lesson_completed")}
    </CompletedItem>
  );
};

export const enum LeftCornerDecoration {
  STANDARD,
  SHARED_FLASHPACKS,
}

export const lessonCornerDecoration = (lesson: Lesson, account: Account) =>
  lesson.custom_flashpack_id && lesson.owner_id !== account.user.id
    ? LeftCornerDecoration.SHARED_FLASHPACKS
    : LeftCornerDecoration.STANDARD;

export const LessonItemBody = ({
  lesson,
  account,
  leftCornerDecoration,
  style,
  className,
  isCompact = false,
}: {
  lesson: Lesson;
  account: Account;
  leftCornerDecoration: LeftCornerDecoration;
  style?: CSSProperties;
  className?: string;
  isCompact?: boolean;
}) => {
  const { t } = useTranslation();
  const coverSize = isCompact ? CELL_COVER_SIZE_COMPACT : CELL_COVER_SIZE;
  const isSharedFlashpack =
    lesson.custom_flashpack_id != null && isSchoolUserTeacher(account.user);
  const isCompleted = didProfileCompleteLesson(
    account.profileState?.completed_lessons_info || {},
    lesson.id,
    lesson.custom_flashpack_id
  );

  const sharedFlashpackMeta = () => {
    const isFromSomeoneElse =
      leftCornerDecoration === LeftCornerDecoration.SHARED_FLASHPACKS ||
      (lesson.shared && lesson.owner_id !== account.user.id);
    if (isFromSomeoneElse) {
      return (
        <CellMetaItem>
          {t("lesson_shared_by", {
            owner:
              lesson.owner_first_name +
              " " +
              lesson.owner_last_name.substring(0, 1),
          })}
        </CellMetaItem>
      );
    }
    return (
      lesson.shared && (
        <CellMetaItem>
          <FontAwesomeIcon icon={faShareNodes} />
          {t("lesson_now_sharing")}
        </CellMetaItem>
      )
    );
  };

  const completedMeta = () => (
    <>
      <CompletedMetaItem />
      <LessonBadges
        height="1.2em"
        lesson={lesson}
        account={account}
        useWhiteIcons={false}
        hideCompletion
      />
    </>
  );

  const lessonAttributeMeta = () => (
    <>
      <CellMetaItem icon="duration">
        {t("lesson_duration_in_minutes", { length: lesson.length_minutes })}
      </CellMetaItem>
      {hasAgeRange(lesson) && (
        <CellMetaItem icon="ageRange">
          {ageRange(t, lesson.age_min, lesson.age_max)}
        </CellMetaItem>
      )}
    </>
  );

  return (
    <StyledLessonItemBody key={lesson.id} style={style} className={className}>
      <Cover
        url={lesson.cover_image}
        alt={t("lesson_cover_content_description", { title: lesson.title })}
        size={coverSize}
        $marginEnd={isCompact ? COVER_MARGIN_END_COMPACT : COVER_MARGIN_END}
        $radius={isCompact ? SURFACE_CARD_RADIUS_SMALL : SURFACE_CARD_RADIUS}
        isElevated
      />
      <MetadataBody $height={coverSize}>
        <Title>{lesson.title}</Title>
        {!isCompact && (
          <Description>
            {isSchoolUserTeacher(account.user)
              ? lesson.teacher_short_description
              : lesson.short_description}
          </Description>
        )}
        <MetaRow $gap={ATTRIBUTE_SPACING}>
          {isSharedFlashpack
            ? sharedFlashpackMeta()
            : isCompleted
            ? completedMeta()
            : lessonAttributeMeta()}
        </MetaRow>
      </MetadataBody>
    </StyledLessonItemBody>
  );
};
